import React, { useState, useEffect, useCallback } from "react";
import {
  ShoppingBag, Plus, Trash2, Pencil, Loader2, RefreshCw, X, Save, Eye, EyeOff
} from "lucide-react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";
const SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

const EMPTY_FORM = { name: "", description: "", price: "", image_url: "", category: "" };

const CATEGORIES = ["Weapon", "Armor", "Consumable", "Cosmetic", "Mount", "Pet", "Currency", "Other"];

export default function StoreManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadingImage, setUploadingImage] = useState(false);
  const token = localStorage.getItem("ka_admin_token");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/store/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load store items");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price ?? "",
      image_url: item.image_url || "",
      category: item.category || "",
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("title", form.name || file.name);
    try {
      const res = await fetch(`${API_URL}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setForm(f => ({ ...f, image_url: `${SERVER_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    try {
      const url = editingItem ? `${API_URL}/store/${editingItem.id}` : `${API_URL}/store`;
      const method = editingItem ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(editingItem ? "Item updated" : "Item created");
      setDialogOpen(false);
      fetchItems();
    } catch {
      toast.error("Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      const res = await fetch(`${API_URL}/store/${item.id}/toggle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_active: i.is_active ? 0 : 1 } : i));
      toast.success(item.is_active ? "Item hidden from store" : "Item visible in store");
    } catch {
      toast.error("Failed to toggle item");
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/store/${item.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Item deleted");
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch {
      toast.error("Failed to delete item");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold flex items-center gap-2 text-white">
          <ShoppingBag className="w-5 h-5 text-[#8B0000]" />
          Store Manager
        </h3>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={fetchItems} disabled={loading} className="text-gray-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={openNew} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Items grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#8B0000] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white/[0.02] border border-dashed border-[#8B0000]/20 rounded-lg">
          <ShoppingBag className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No store items yet. Add your first one.</p>
          <Button onClick={openNew} className="mt-4 bg-[#8B0000] hover:bg-[#B22222] text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`bg-[#1a1a1a]/80 border-[#8B0000]/20 overflow-hidden transition-all ${
                !item.is_active ? "opacity-50" : "hover:border-[#8B0000]/50"
              }`}
            >
              {/* Image */}
              <div className="h-44 bg-black/40 relative overflow-hidden">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag className="w-10 h-10 text-gray-700" />
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  <span className="px-2 py-0.5 bg-[#8B0000] text-white text-xs font-bold rounded">
                    {item.price} KC
                  </span>
                  {!item.is_active && (
                    <span className="px-2 py-0.5 bg-black/70 text-gray-400 text-xs rounded border border-white/10">
                      Hidden
                    </span>
                  )}
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-semibold text-white leading-tight">{item.name}</h4>
                  {item.category && (
                    <Badge className="bg-white/5 text-gray-400 border border-white/10 text-xs shrink-0">
                      {item.category}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
              </CardContent>

              <CardFooter className="p-3 pt-0 flex justify-end gap-1">
                <Button
                  size="sm" variant="ghost"
                  onClick={() => handleToggle(item)}
                  title={item.is_active ? "Hide from store" : "Show in store"}
                  className="text-gray-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                >
                  {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => openEdit(item)}
                  className="text-gray-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => handleDelete(item)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8 p-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111111] border-[#8B0000]/30 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#8B0000]" />
              {editingItem ? "Edit Item" : "New Store Item"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm text-gray-400">Name *</label>
                <Input
                  className="bg-white/5 border-[#8B0000]/30 text-white"
                  placeholder="Item name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-gray-400">Price (KC) *</label>
                <Input
                  type="number"
                  className="bg-white/5 border-[#8B0000]/30 text-white"
                  placeholder="0"
                  value={form.price}
                  onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-gray-400">Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, category: f.category === cat ? "" : cat }))}
                    className={`px-3 py-1 rounded text-xs border transition-colors ${
                      form.category === cat
                        ? "bg-[#8B0000]/30 border-[#8B0000]/60 text-white"
                        : "bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-gray-400">Description</label>
              <Textarea
                className="bg-white/5 border-[#8B0000]/30 text-white min-h-[80px]"
                placeholder="Item description..."
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">Image</label>
              {/* Upload button */}
              <div className="flex gap-2">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#8B0000]/30 bg-white/5 text-sm text-gray-300 hover:text-white hover:bg-white/10 cursor-pointer transition-colors">
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {uploadingImage ? "Uploading..." : "Upload Image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    disabled={uploadingImage}
                  />
                </label>
                <span className="text-gray-600 text-sm self-center">or paste URL:</span>
              </div>
              <Input
                className="bg-white/5 border-[#8B0000]/30 text-white"
                placeholder="https://..."
                value={form.image_url}
                onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))}
              />
              {form.image_url && (
                <div className="h-36 rounded-lg overflow-hidden border border-[#8B0000]/20">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingItem ? "Save Changes" : "Add to Store"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
